import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

export type AppsFlyerAttributionValue = string | number | boolean | null;

export interface AppsFlyerAttributionPayload {
  status?: string;
  source?: string;
  error?: string;
  is_deferred?: boolean;
  deep_link_value?: AppsFlyerAttributionValue;
  data?: Record<string, AppsFlyerAttributionValue>;
  [key: string]: AppsFlyerAttributionValue | Record<string, AppsFlyerAttributionValue> | undefined;
}

/** Google Play Install Referrer, as read by the Android plugin. */
export interface NativePlayInstallReferrer {
  status: string;
  installReferrer?: string | null;
  referrerClickTimestampSeconds?: number | null;
  installBeginTimestampSeconds?: number | null;
  googlePlayInstantParam?: boolean | null;
}

interface AppsFlyerAttributionPlugin {
  getAppsFlyerId(): Promise<{ appsflyerId: string | null }>;
  setCustomerUserId(options: { userId: string }): Promise<{ updated: boolean }>;
  setUserEmails(options: { email: string }): Promise<{ updated: boolean; reason?: string }>;
  setAnonymizeUser(options: { anonymize: boolean }): Promise<{ updated: boolean }>;
  logEvent(options: {
    eventName: string;
    eventValues: Record<string, string | number | boolean>;
  }): Promise<{
    logged: boolean;
    error?: string;
    errorCode?: number;
    errorDescription?: string;
  }>;
  getLatestDeepLink(): Promise<{ payload: AppsFlyerAttributionPayload | null }>;
  getLatestConversionData(): Promise<{ payload: AppsFlyerAttributionPayload | null }>;
  getInstallReferrer(): Promise<NativePlayInstallReferrer>;
  addListener(
    eventName: 'deepLink' | 'conversionData',
    listenerFunc: (payload: AppsFlyerAttributionPayload) => void
  ): Promise<PluginListenerHandle>;
}

const AppsFlyerAttribution = registerPlugin<AppsFlyerAttributionPlugin>('AppsFlyerAttribution');

export function isNativeAppsFlyerRuntime(): boolean {
  return (
    Capacitor.isNativePlatform() &&
    ['android', 'ios'].includes(Capacitor.getPlatform())
  );
}

export async function getNativeAppsFlyerId(): Promise<string | null> {
  if (!isNativeAppsFlyerRuntime()) return null;

  const { appsflyerId } = await AppsFlyerAttribution.getAppsFlyerId();
  return typeof appsflyerId === 'string' && appsflyerId.trim().length > 0
    ? appsflyerId.trim()
    : null;
}

// There is no way to clear the native customer user id today: both native
// plugins reject an empty id, and the iOS plugin has no anonymize call. On a
// user change or ad data opt-out, Android is anonymized instead
// (setNativeAppsFlyerAnonymized); on iOS the previous id stays on the SDK
// until a later native plugin change adds a clear (documented in ARCHI). The
// id is only ever set for a member the ad data policy allows.
export async function setNativeAppsFlyerCustomerUserId(userId: string): Promise<boolean> {
  if (!isNativeAppsFlyerRuntime()) return false;

  const trimmed = userId.trim();
  if (!trimmed) return false;

  const { updated } = await AppsFlyerAttribution.setCustomerUserId({ userId: trimmed });
  return updated;
}

// Feeds AppsFlyer's advanced matching: the native SDK hashes the address with
// SHA-256 on device and shares only the hash with partners, and only where the
// integration's Advanced Matching Data Sharing toggle is on. iOS additionally
// requires ATT authorization (enforced in the native plugin).
export async function setNativeAppsFlyerUserEmail(email: string): Promise<boolean> {
  if (!isNativeAppsFlyerRuntime()) return false;

  const normalized = email.trim().toLowerCase();
  if (!normalized || !normalized.includes('@')) return false;

  const { updated } = await AppsFlyerAttribution.setUserEmails({ email: normalized });
  return updated;
}

// Android only: an opt-out in Your Privacy Choices anonymizes the native SDK
// (no device identifiers on sessions or events) and a later opt-in restores
// it. iOS gates identifiers on the ATT answer natively; web has no SDK.
export async function setNativeAppsFlyerAnonymized(anonymize: boolean): Promise<boolean> {
  if (!isNativeAppsFlyerRuntime() || Capacitor.getPlatform() !== 'android') return false;

  const { updated } = await AppsFlyerAttribution.setAnonymizeUser({ anonymize });
  return updated;
}

export async function logNativeAppsFlyerEvent(
  eventName: string,
  eventValues: Record<string, unknown> = {}
): Promise<boolean> {
  if (!isNativeAppsFlyerRuntime()) return false;

  const trimmed = eventName.trim();
  if (!trimmed) return false;

  const normalizedValues = Object.fromEntries(
    Object.entries(eventValues).filter(([, value]) =>
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    )
  ) as Record<string, string | number | boolean>;

  const { logged } = await AppsFlyerAttribution.logEvent({
    eventName: trimmed,
    eventValues: normalizedValues,
  });
  return logged;
}

export async function getLatestNativeAppsFlyerDeepLink(): Promise<AppsFlyerAttributionPayload | null> {
  if (!isNativeAppsFlyerRuntime()) return null;

  const { payload } = await AppsFlyerAttribution.getLatestDeepLink();
  return payload && typeof payload === 'object' ? payload : null;
}

export async function getLatestNativeAppsFlyerConversionData(): Promise<AppsFlyerAttributionPayload | null> {
  if (!isNativeAppsFlyerRuntime()) return null;

  const { payload } = await AppsFlyerAttribution.getLatestConversionData();
  return payload && typeof payload === 'object' ? payload : null;
}

// Android only. The plugin always resolves with a status; a bridge failure
// comes back as 'plugin_error' so the caller can still record it.
export async function getNativePlayInstallReferrer(): Promise<NativePlayInstallReferrer | null> {
  if (!isNativeAppsFlyerRuntime() || Capacitor.getPlatform() !== 'android') return null;

  try {
    const result = await AppsFlyerAttribution.getInstallReferrer();
    return result && typeof result.status === 'string' ? result : { status: 'plugin_error' };
  } catch {
    return { status: 'plugin_error' };
  }
}

export async function addNativeAppsFlyerDeepLinkListener(
  listener: (payload: AppsFlyerAttributionPayload) => void
): Promise<PluginListenerHandle | null> {
  if (!isNativeAppsFlyerRuntime()) return null;
  return AppsFlyerAttribution.addListener('deepLink', listener);
}

export async function addNativeAppsFlyerConversionDataListener(
  listener: (payload: AppsFlyerAttributionPayload) => void
): Promise<PluginListenerHandle | null> {
  if (!isNativeAppsFlyerRuntime()) return null;
  return AppsFlyerAttribution.addListener('conversionData', listener);
}
